import {
  computed,
  effect,
  type ReadonlySignal,
  signal,
} from '@preact/signals-core';

import { BatchTaskManager } from './batch-task-manager';
import { VirtualElementWrapper } from './virtual-cell';

const RESTORED_ROW_HEIGHT_SETTLE_FRAMES = 1;
const SCROLLING_ROW_HEIGHT_SHRINK_DEFER_FLUSHES = 1;

function traceTableLayout(event: string, data: Record<string, unknown>): void {
  try {
    if (
      typeof performance === 'undefined' ||
      globalThis.location?.hostname !== 'localhost'
    ) {
      return;
    }
    performance.mark(`sl-table-layout:${event}:${JSON.stringify(data)}`);
  } catch {
    // Debug-only trace marker. Never affect table rendering.
  }
}

function getColumnLayoutSignature(
  columns: ReadonlyArray<{ id: string; width: number }>
): string {
  return columns.map(column => `${column.id}:${column.width}`).join('|');
}

export interface Disposable {
  dispose(): void;
}

export function normalizeMeasuredRowHeight(
  measuredHeight: number,
  estimatedRowHeight: number
): number {
  return Math.max(measuredHeight, estimatedRowHeight);
}

export function getVirtualScrollVerticalPreloadSize(
  viewportHeight: number
): number {
  return Math.max(600, viewportHeight * 2);
}

export class NodeLifeCycle implements Disposable {
  disposables: (() => void)[] = [];
  init() {}
  isDisposed = false;
  dispose() {
    if (this.isDisposed) {
      return;
    }
    this.isDisposed = true;
    this.disposables.forEach(disposable => disposable());
  }
}

export class GridNode<Data> extends NodeLifeCycle {
  private _data?: Data;
  get data(): Data {
    if (!this._data) {
      this._data = this.initData();
    }
    return this._data;
  }
  constructor(private readonly initData: () => Data) {
    super();
  }
}

export class CacheManager<K, V extends Disposable> {
  constructor(readonly keyToString: (key: K) => string) {}
  protected readonly cache = new Map<string, V>();

  getOrCreate(key: K, create: () => V): V {
    const stringKey = this.keyToString(key);
    let value = this.cache.get(stringKey);
    if (!value) {
      value = create();
      this.cache.set(stringKey, value);
    }
    return value;
  }

  has(key: K): boolean {
    return this.cache.has(this.keyToString(key));
  }

  delete(key: K): void {
    const value = this.cache.get(this.keyToString(key));
    if (value) {
      value.dispose();
      this.cache.delete(this.keyToString(key));
    }
  }

  clear(): void {
    for (const value of this.cache.values()) {
      value.dispose();
    }
    this.cache.clear();
  }

  cleanup(activeKeys: Set<string>): void {
    const toDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (!activeKeys.has(key)) {
        toDelete.push(key);
      }
    }
    for (const key of toDelete) {
      this.cache.get(key)?.dispose();
      this.cache.delete(key);
    }
  }

  cleanupValues(shouldKeep: (value: V) => boolean): void {
    const toDelete: string[] = [];
    for (const [key, value] of this.cache) {
      if (!shouldKeep(value)) {
        toDelete.push(key);
      }
    }
    for (const key of toDelete) {
      this.cache.get(key)?.dispose();
      this.cache.delete(key);
    }
  }
}

export abstract class VirtualScroll extends NodeLifeCycle {
  readonly container: VirtualScrollContainer;

  constructor(containerOptions: VirtualScrollOptions) {
    super();
    this.container = new VirtualScrollContainer(containerOptions);
  }

  override dispose() {
    super.dispose();
    this.container.dispose();
  }
}

export class GridCell<GroupData, RowData, CellData> extends GridNode<CellData> {
  readonly renderTask;
  readonly element;
  readonly columnIndex$ = computed(() => {
    return this.row.grid.columns$.value.findIndex(
      column => column.id === this.columnId
    );
  });

  private readonly realHeight$ = signal<number>();
  readonly contentHeight$ = computed(() => {
    return this.realHeight$.value;
  });
  private readonly columnPosition$ = computed(() => {
    return this.row.grid.columnPosition$.value[this.columnIndex$.value];
  });
  readonly height$ = computed(
    () =>
      this.grid.fixedRowHeight$.value ??
      this.contentHeight$.value ??
      this.grid.estimatedRowHeight$.value
  );
  readonly width$ = computed(() => this.columnPosition$.value?.width);
  readonly left$ = computed(() => this.columnPosition$.value?.left);
  readonly top$ = computed(() => this.row.top$.value);
  readonly right$ = computed(() => {
    return this.columnPosition$.value?.right;
  });
  readonly bottom$ = computed(() => {
    const top = this.top$.value;
    if (top == null) {
      return;
    }
    // Cells render in wrappers sized to the row height. Visibility must use
    // that same row-height box; otherwise short cells in a tall row get
    // removed while scrolling inside the row, which also removes their column
    // separator.
    const height = this.row.height$.value;
    if (height == null) {
      return;
    }
    return top + height;
  });

  get rowIndex$() {
    return this.row.rowIndex$;
  }

  get grid() {
    return this.row.grid;
  }

  constructor(
    readonly row: GridRow<GroupData, RowData, CellData>,
    readonly columnId: string,
    createElement: (
      cell: GridCell<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement,
    initCellData: (cell: GridCell<GroupData, RowData, CellData>) => CellData
  ) {
    super(() => initCellData(this));
    this.element = new VirtualElementWrapper();
    this.element.rect = {
      left$: this.left$,
      top$: this.top$,
      width$: this.width$,
      height$: this.row.height$,
    };
    this.element.updateHeight = height => this.updateHeight(height);
    this.element.element = createElement(this, this.element);
    const isInit = computed(() => {
      return this.height$.value != null;
    });
    this.renderTask = this.grid.container.initElement(this.element, isInit);
    let stopInitialVisibilityEffect: (() => void) | undefined;
    let stopInitialVisibilityQueued = false;
    stopInitialVisibilityEffect = effect(() => {
      if (isInit.value && !this.isVisible$.peek()) {
        this.renderTask.hide();
        if (!stopInitialVisibilityQueued) {
          stopInitialVisibilityQueued = true;
          queueMicrotask(() => stopInitialVisibilityEffect?.());
        }
      }
    });
    this.disposables.push(() => stopInitialVisibilityEffect?.());
    this.disposables.push(
      effect(() => {
        this.checkRender();
      })
    );
  }

  isVisible$ = computed(() => {
    const height = this.height$.value;
    if (height == null) {
      return false;
    }
    const offsetTop = this.top$.value;
    if (offsetTop == null) {
      return false;
    }
    const offsetBottom = this.bottom$.value;
    if (offsetBottom == null) {
      return false;
    }
    const offsetLeft = this.left$.value ?? 0;
    const offsetRight = this.right$.value ?? 0;
    const viewport = this.grid.container.viewport$.value;
    const xInView =
      offsetRight >= viewport.left && offsetLeft <= viewport.right;
    const yInView =
      offsetBottom >= viewport.top && offsetTop <= viewport.bottom;
    const isVisible = xInView && yInView;
    return isVisible;
  });

  checkRender() {
    const isVisible = this.isVisible$.value;
    if (isVisible) {
      this.renderTask.show();
    } else if (!isVisible && this.element.isConnected) {
      this.renderTask.hide();
    }
  }

  updateHeight(height: number) {
    this.realHeight$.value = height;
    this.row.updateCellHeight(this.columnId, height);
  }

  override dispose() {
    super.dispose();
    this.renderTask.cancel();
    this.element.remove();
  }
}

export class GridRow<GroupData, RowData, CellData> extends GridNode<RowData> {
  cells$ = computed(() => {
    return this.grid.columns$.value.map(column => {
      return this.grid.getOrCreateCell(this, column.id);
    });
  });

  rowIndex$: ReadonlySignal<number> = computed(() => {
    return this.group.getRowIndex(this.rowId);
  });

  prevRow$: ReadonlySignal<GridRow<GroupData, RowData, CellData> | undefined> =
    computed(() => {
      const prevRowId = this.group.rowIds$.value[this.rowIndex$.value - 1];
      if (!prevRowId) {
        return;
      }
      return this.group.grid.getOrCreateRow(this.group, prevRowId);
    });

  get grid() {
    return this.group.grid;
  }

  top$: ReadonlySignal<number | undefined> = computed(() => {
    return this.group.getRowTop(this.rowId);
  });

  bottom$ = computed(() => {
    const top = this.top$.value;
    if (top == null) {
      return;
    }
    const height = this.height$.value;
    if (height == null) {
      return;
    }
    return top + height;
  });

  height$ = computed(() => {
    return this.group.getRowHeight(this.rowId);
  });

  constructor(
    readonly group: GridGroup<GroupData, RowData, CellData>,
    readonly rowId: string,
    initRowData: (row: GridRow<GroupData, RowData, CellData>) => RowData
  ) {
    super(() => initRowData(this));
  }

  override dispose() {
    super.dispose();
  }

  updateCellHeight(columnId: string, height: number) {
    this.group.updateCellHeight(this.rowId, columnId, height);
  }
}
export class GroupNode<GroupData, RowData, CellData> extends NodeLifeCycle {
  readonly renderTask;
  readonly height$ = signal<number | undefined>();
  readonly bottom$ = computed(() => {
    const top = this.top$.value;
    const height = this.height$.value;
    if (top == null) {
      return;
    }
    if (height == null) {
      return;
    }
    return top + height;
  });
  constructor(
    public readonly group: GridGroup<GroupData, RowData, CellData>,
    public readonly top$: ReadonlySignal<number | undefined>,
    content: (
      group: GridGroup<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement,
    readonly visibleCheck: (
      node: GroupNode<GroupData, RowData, CellData>
    ) => boolean
  ) {
    super();
    const element = new VirtualElementWrapper();
    element.rect = {
      left$: signal(0),
      top$,
      width$: this.group.grid.totalWidth$,
      height$: this.height$,
    };
    element.element = content(this.group, element);
    element.updateHeight = height => {
      this.height$.value = height;
    };
    const isInit = computed(() => {
      return this.height$.value != null;
    });
    this.renderTask = this.container.initElement(element, isInit);
    const cancel = effect(() => {
      if (isInit.value && !this.isVisible$.peek()) {
        this.renderTask.hide();
        cancel();
      }
    });
    this.disposables.push(
      effect(() => {
        this.checkRender();
      })
    );
  }
  get container() {
    return this.group.grid.container;
  }

  isVisible$ = computed(() => {
    return this.visibleCheck(this);
  });

  checkRender() {
    const isVisible = this.isVisible$.value;
    if (isVisible) {
      this.renderTask.show();
    } else {
      this.renderTask.hide();
    }
  }
}

export class GridGroup<
  GroupData,
  RowData,
  CellData,
> extends GridNode<GroupData> {
  private readonly rowCellHeights = new Map<string, Map<string, number>>();

  private readonly rowHeights$ = signal<ReadonlyMap<string, number>>(new Map());

  private readonly pendingRowHeights = new Map<string, number | undefined>();

  private readonly restoredRowHeights = new Set<string>();

  private readonly scrollingShrinkDefers = new Map<string, number>();

  private rowHeightFlushScheduled = false;

  private rowHeightFlushTask:
    | ReturnType<typeof requestAnimationFrame>
    | ReturnType<typeof setTimeout>
    | undefined;

  top$: ReadonlySignal<number | undefined> = computed(() => {
    const prevGroup = this.prevGroup$.value;
    if (!prevGroup) {
      return 0;
    }
    return prevGroup.bottom$.value;
  });
  topNode = new GroupNode(this, this.top$, this.topElement, node => {
    const height = node.height$.value;
    if (height == null) {
      return false;
    }
    const top = this.top$.value;
    if (top == null) {
      return false;
    }
    const bottom = this.lastRowBottom$.value ?? top + height;
    const groupInView =
      top < this.grid.container.viewport$.value.bottom &&
      bottom > this.grid.container.viewport$.value.top;
    return groupInView;
  });
  lastRowBottom$: ReadonlySignal<number | undefined> = computed(() => {
    return this.rowMetrics$.value?.bottom;
  });
  bottomNode = new GroupNode(
    this,
    this.lastRowBottom$,
    this.bottomElement,
    node => {
      const height = node.height$.value;
      if (height == null) {
        return false;
      }
      const top = this.lastRowBottom$.value;
      if (top == null) {
        return false;
      }
      const bottom = top + height;
      const groupInView =
        top < this.grid.container.viewport$.value.bottom &&
        bottom > this.grid.container.viewport$.value.top;
      return groupInView;
    }
  );
  rowIds$: ReadonlySignal<string[]> = computed(() => {
    const group = this.grid.options.groups$.value.find(
      g => g.id === this.groupId
    );
    if (!group) {
      return [];
    }
    return group.rows;
  });

  private readonly rowIndex$ = computed(() => {
    const indexes = new Map<string, number>();
    this.rowIds$.value.forEach((rowId, index) => {
      indexes.set(rowId, index);
    });
    return indexes;
  });

  private readonly rowMetrics$ = computed(() => {
    const rowsTop = this.rowsTop$.value;
    if (rowsTop == null) {
      return;
    }
    const rowCount = this.rowIds$.value.length;
    const fixedRowHeight = this.grid.fixedRowHeight$.value;
    const estimatedRowHeight = this.grid.estimatedRowHeight$.value;
    if (fixedRowHeight != null) {
      return {
        baseRowHeight: fixedRowHeight,
        bottom: rowsTop + rowCount * fixedRowHeight,
        overrideIndexes: [] as number[],
        prefixDeltas: [] as number[],
        rowsTop,
      };
    }

    const rowIndex = this.rowIndex$.value;
    const overrides: { delta: number; index: number }[] = [];
    for (const [rowId, rowHeight] of this.rowHeights$.value) {
      const index = rowIndex.get(rowId);
      if (index == null) {
        continue;
      }
      const delta = rowHeight - estimatedRowHeight;
      if (delta !== 0) {
        overrides.push({ delta, index });
      }
    }
    overrides.sort((a, b) => a.index - b.index);

    const overrideIndexes: number[] = [];
    const prefixDeltas: number[] = [];
    let totalDelta = 0;
    for (const override of overrides) {
      totalDelta += override.delta;
      overrideIndexes.push(override.index);
      prefixDeltas.push(totalDelta);
    }

    return {
      baseRowHeight: estimatedRowHeight,
      bottom: rowsTop + rowCount * estimatedRowHeight + totalDelta,
      overrideIndexes,
      prefixDeltas,
      rowsTop,
    };
  });

  rows$: ReadonlySignal<GridRow<GroupData, RowData, CellData>[]> = computed(
    () => {
      return this.rowIds$.value.map(rowId => {
        return this.grid.getOrCreateRow(this, rowId);
      });
    }
  );

  groupIndex$: ReadonlySignal<number> = computed(() => {
    return this.grid.groupIds$.value.findIndex(
      groupId => groupId === this.groupId
    );
  });

  prevGroup$: ReadonlySignal<
    GridGroup<GroupData, RowData, CellData> | undefined
  > = computed(() => {
    const prevGroupId = this.grid.groupIds$.value[this.groupIndex$.value - 1];
    if (prevGroupId == null) {
      return;
    }
    return this.grid.getOrCreateGroup(prevGroupId);
  });

  get rowsTop$() {
    return this.topNode.bottom$;
  }

  get bottomNodeTop$() {
    return this.lastRowBottom$;
  }

  height$ = computed(() => {
    const bottom = this.bottom$.value;
    if (bottom == null) {
      return;
    }
    const top = this.top$.value;
    if (top == null) {
      return;
    }
    return bottom - top;
  });

  bottom$ = computed(() => {
    return this.bottomNode.bottom$.value;
  });

  constructor(
    readonly grid: GridVirtualScroll<GroupData, RowData, CellData>,
    readonly groupId: string,
    readonly topElement: (
      group: GridGroup<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement,
    readonly bottomElement: (
      group: GridGroup<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement,
    initGroupData: (group: GridGroup<GroupData, RowData, CellData>) => GroupData
  ) {
    super(() => initGroupData(this));
    if (grid.options.initialRowHeights) {
      this.rowHeights$.value = new Map(grid.options.initialRowHeights);
      this.restoredRowHeights.clear();
      for (const rowId of grid.options.initialRowHeights.keys()) {
        this.restoredRowHeights.add(rowId);
      }
      traceTableLayout('group-cache-init', {
        groupId,
        rowCount: grid.options.initialRowHeights.size,
        rows: Array.from(grid.options.initialRowHeights.entries()).slice(0, 12),
      });
    }
    let columnLayoutSignature = getColumnLayoutSignature(
      this.grid.columns$.value
    );
    this.disposables.push(
      effect(() => {
        const nextColumnLayoutSignature = getColumnLayoutSignature(
          this.grid.columns$.value
        );
        if (nextColumnLayoutSignature === columnLayoutSignature) {
          return;
        }
        columnLayoutSignature = nextColumnLayoutSignature;
        if (this.restoredRowHeights.size > 0) {
          traceTableLayout('restored-row-height-invalidate-column-layout', {
            groupId: this.groupId,
            restoredRowCount: this.restoredRowHeights.size,
          });
          this.invalidateRestoredRowHeights();
        }
        this.reconcileCellHeights(
          new Set(this.grid.columns$.value.map(column => column.id))
        );
      })
    );
  }

  override dispose() {
    super.dispose();
    if (this.rowHeightFlushTask != null) {
      cancelAnimationFrame(this.rowHeightFlushTask as number);
      clearTimeout(this.rowHeightFlushTask);
      this.rowHeightFlushTask = undefined;
    }
  }

  getRowHeight(rowId: string): number {
    return (
      this.grid.fixedRowHeight$.value ??
      this.rowHeights$.value.get(rowId) ??
      this.grid.estimatedRowHeight$.value
    );
  }

  private invalidateRestoredRowHeights(): void {
    let shouldFlush = false;
    for (const rowId of this.restoredRowHeights) {
      if (this.rowHeights$.peek().has(rowId)) {
        this.pendingRowHeights.set(rowId, undefined);
        this.scrollingShrinkDefers.delete(rowId);
        shouldFlush = true;
      }
    }
    this.restoredRowHeights.clear();
    if (shouldFlush) {
      this.scheduleRowHeightFlush();
    }
  }

  private reconcileCellHeights(activeColumnIds: ReadonlySet<string>): void {
    let shouldFlush = false;
    for (const [rowId, cellHeights] of this.rowCellHeights) {
      let changed = false;
      for (const columnId of cellHeights.keys()) {
        if (!activeColumnIds.has(columnId)) {
          cellHeights.delete(columnId);
          changed = true;
        }
      }
      if (!changed) {
        continue;
      }
      if (cellHeights.size === 0) {
        this.rowCellHeights.delete(rowId);
      }
      const measuredHeight =
        cellHeights.size > 0
          ? Math.max(...cellHeights.values())
          : this.grid.estimatedRowHeight$.value;
      const nextRowHeight =
        measuredHeight === this.grid.estimatedRowHeight$.value
          ? undefined
          : measuredHeight;
      if (this.rowHeights$.peek().get(rowId) === nextRowHeight) {
        this.pendingRowHeights.delete(rowId);
        continue;
      }
      this.pendingRowHeights.set(rowId, nextRowHeight);
      this.scrollingShrinkDefers.delete(rowId);
      shouldFlush = true;
    }
    if (shouldFlush) {
      this.scheduleRowHeightFlush();
    }
  }

  getRowIndex(rowId: string): number {
    return this.rowIndex$.value.get(rowId) ?? -1;
  }

  getRowTop(rowId: string): number | undefined {
    const rowIndex = this.getRowIndex(rowId);
    if (rowIndex < 0) {
      return;
    }
    return this.getRowTopByIndex(rowIndex);
  }

  getRowTopByIndex(rowIndex: number): number | undefined {
    const metrics = this.rowMetrics$.value;
    if (!metrics) {
      return;
    }
    return (
      metrics.rowsTop +
      rowIndex * metrics.baseRowHeight +
      this.getRowHeightDeltaBeforeIndex(rowIndex)
    );
  }

  getVisibleRowIds(viewport: { bottom: number; top: number }): string[] {
    const metrics = this.rowMetrics$.value;
    if (!metrics) {
      return [];
    }

    const rowIds = this.rowIds$.value;
    const visibleRowIds: string[] = [];
    let low = 0;
    let high = rowIds.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      const top =
        metrics.rowsTop +
        mid * metrics.baseRowHeight +
        this.getRowHeightDeltaBeforeIndex(mid);
      const rowId = rowIds[mid];
      if (rowId == null) {
        break;
      }
      const bottom = top + this.getRowHeight(rowId);
      if (bottom < viewport.top) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    for (let rowIndex = low; rowIndex < rowIds.length; rowIndex++) {
      const top =
        metrics.rowsTop +
        rowIndex * metrics.baseRowHeight +
        this.getRowHeightDeltaBeforeIndex(rowIndex);
      const rowId = rowIds[rowIndex];
      if (rowId == null) {
        continue;
      }
      const bottom = top + this.getRowHeight(rowId);
      if (bottom < viewport.top) {
        continue;
      }
      if (top > viewport.bottom) {
        break;
      }
      visibleRowIds.push(rowId);
    }
    return visibleRowIds;
  }

  private getRowHeightDeltaBeforeIndex(rowIndex: number): number {
    const metrics = this.rowMetrics$.value;
    if (!metrics || metrics.overrideIndexes.length === 0) {
      return 0;
    }

    let low = 0;
    let high = metrics.overrideIndexes.length;
    while (low < high) {
      const mid = (low + high) >> 1;
      if (
        (metrics.overrideIndexes[mid] ?? Number.POSITIVE_INFINITY) < rowIndex
      ) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }
    return low === 0 ? 0 : (metrics.prefixDeltas[low - 1] ?? 0);
  }

  updateCellHeight(
    rowId: string,
    columnId: string,
    height: number,
    options: {
      allowCachedShrink?: boolean;
      preserveRestoredRowHeight?: boolean;
    } = {}
  ): void {
    if (this.grid.fixedRowHeight$.value != null) {
      return;
    }

    const measuredHeight = normalizeMeasuredRowHeight(
      height,
      this.grid.estimatedRowHeight$.value
    );
    // Imperative measurement updates can run inside effects that intentionally
    // watch cell values. Reading rowHeights$ with `.value` here would subscribe
    // those effects to the height output they update, creating feedback loops
    // (Firefox trace 2026-06-10: relation estimate 168px <-> DOM measure 166px).
    const currentRowHeight = this.rowHeights$.peek().get(rowId);
    if (
      options.preserveRestoredRowHeight &&
      currentRowHeight != null &&
      this.restoredRowHeights.has(rowId)
    ) {
      traceTableLayout('height-preserve-restored-skip', {
        groupId: this.groupId,
        rowId,
        columnId,
        measuredHeight,
        currentRowHeight,
        options,
      });
      return;
    }
    let cellHeights = this.rowCellHeights.get(rowId);
    if (!cellHeights) {
      cellHeights = new Map();
      this.rowCellHeights.set(rowId, cellHeights);
    }
    const cellHeightUnchanged = cellHeights.get(columnId) === measuredHeight;
    if (!cellHeightUnchanged) {
      cellHeights.set(columnId, measuredHeight);
    }

    const rowHeight = Math.max(...cellHeights.values());
    const nextRowHeight =
      rowHeight === this.grid.estimatedRowHeight$.value ? undefined : rowHeight;
    const currentEffectiveHeight =
      currentRowHeight ?? this.grid.estimatedRowHeight$.value;
    const nextEffectiveHeight =
      nextRowHeight ?? this.grid.estimatedRowHeight$.value;
    if (
      !options.allowCachedShrink &&
      this.restoredRowHeights.has(rowId) &&
      nextEffectiveHeight < currentEffectiveHeight
    ) {
      traceTableLayout('height-restored-shrink-defer', {
        groupId: this.groupId,
        rowId,
        columnId,
        measuredHeight,
        currentRowHeight,
        nextRowHeight,
        currentEffectiveHeight,
        nextEffectiveHeight,
        options,
      });
    }
    if (currentRowHeight === nextRowHeight) {
      traceTableLayout('height-current-match', {
        groupId: this.groupId,
        rowId,
        columnId,
        measuredHeight,
        currentRowHeight,
        nextRowHeight,
        hadPending: this.pendingRowHeights.has(rowId),
        options,
      });
      this.pendingRowHeights.delete(rowId);
      this.scrollingShrinkDefers.delete(rowId);
      return;
    }
    if (
      this.pendingRowHeights.has(rowId) &&
      this.pendingRowHeights.get(rowId) === nextRowHeight
    ) {
      return;
    }
    this.pendingRowHeights.set(rowId, nextRowHeight);
    if (nextEffectiveHeight >= currentEffectiveHeight) {
      this.scrollingShrinkDefers.delete(rowId);
    }
    const shouldSettleRestoredRowHeight =
      this.restoredRowHeights.has(rowId) && !options.allowCachedShrink;
    traceTableLayout('height-pending', {
      groupId: this.groupId,
      rowId,
      columnId,
      measuredHeight,
      rowHeight,
      currentRowHeight,
      nextRowHeight,
      currentEffectiveHeight,
      nextEffectiveHeight,
      restored: this.restoredRowHeights.has(rowId),
      shouldSettleRestoredRowHeight,
      options,
    });
    if (
      this.grid.container.isScrolling$.peek() &&
      nextEffectiveHeight > currentEffectiveHeight
    ) {
      this.flushRowHeights({
        allowWhileScrolling: true,
        onlyGrowing: true,
      });
      if (this.pendingRowHeights.size > 0) {
        this.scheduleRowHeightFlush();
      }
      return;
    }
    this.scheduleRowHeightFlush({
      settleFrames: shouldSettleRestoredRowHeight
        ? RESTORED_ROW_HEIGHT_SETTLE_FRAMES
        : 0,
    });
  }

  private flushRowHeights(
    options: {
      allowWhileScrolling?: boolean;
      onlyGrowing?: boolean;
      matureScrollingShrinkRows?: ReadonlySet<string>;
    } = {}
  ): void {
    this.rowHeightFlushScheduled = false;
    this.rowHeightFlushTask = undefined;
    if (
      this.grid.container.isScrolling$.peek() &&
      !options.allowWhileScrolling
    ) {
      const matureScrollingShrinkRows = new Set<string>();
      for (const [rowId, rowHeight] of this.pendingRowHeights) {
        const currentEffectiveHeight =
          this.rowHeights$.value.get(rowId) ??
          this.grid.estimatedRowHeight$.value;
        const nextEffectiveHeight =
          rowHeight ?? this.grid.estimatedRowHeight$.value;
        if (nextEffectiveHeight >= currentEffectiveHeight) {
          this.scrollingShrinkDefers.delete(rowId);
          continue;
        }
        const defers = (this.scrollingShrinkDefers.get(rowId) ?? 0) + 1;
        this.scrollingShrinkDefers.set(rowId, defers);
        if (defers > SCROLLING_ROW_HEIGHT_SHRINK_DEFER_FLUSHES) {
          matureScrollingShrinkRows.add(rowId);
        }
      }
      if (matureScrollingShrinkRows.size > 0) {
        traceTableLayout('flush-allow-scrolling-shrink', {
          groupId: this.groupId,
          rows: Array.from(matureScrollingShrinkRows),
          pending: Array.from(this.pendingRowHeights.entries()),
        });
        this.flushRowHeights({
          allowWhileScrolling: true,
          matureScrollingShrinkRows,
        });
        return;
      }
      traceTableLayout('flush-defer-scrolling', {
        groupId: this.groupId,
        pending: Array.from(this.pendingRowHeights.entries()),
        options,
      });
      this.scheduleRowHeightFlush();
      return;
    }
    if (this.pendingRowHeights.size === 0) {
      traceTableLayout('flush-empty', {
        groupId: this.groupId,
        options,
      });
      return;
    }

    const next = new Map(this.rowHeights$.value);
    let changed = false;
    const appliedRowIds: string[] = [];
    const appliedRows: {
      from: number | undefined;
      rowId: string;
      to: number | undefined;
    }[] = [];
    for (const [rowId, rowHeight] of this.pendingRowHeights) {
      if (
        options.matureScrollingShrinkRows &&
        !options.matureScrollingShrinkRows.has(rowId)
      ) {
        continue;
      }
      if (options.onlyGrowing) {
        const currentEffectiveHeight =
          next.get(rowId) ?? this.grid.estimatedRowHeight$.value;
        const nextEffectiveHeight =
          rowHeight ?? this.grid.estimatedRowHeight$.value;
        if (nextEffectiveHeight <= currentEffectiveHeight) {
          continue;
        }
      }
      const previousRowHeight = next.get(rowId);
      if (rowHeight == null) {
        if (next.delete(rowId)) {
          changed = true;
        }
      } else if (next.get(rowId) !== rowHeight) {
        next.set(rowId, rowHeight);
        changed = true;
      }
      appliedRowIds.push(rowId);
      appliedRows.push({
        from: previousRowHeight,
        rowId,
        to: rowHeight,
      });
    }
    for (const rowId of appliedRowIds) {
      this.pendingRowHeights.delete(rowId);
      this.restoredRowHeights.delete(rowId);
      this.scrollingShrinkDefers.delete(rowId);
    }
    if (changed) {
      traceTableLayout('flush-apply', {
        groupId: this.groupId,
        options,
        rows: appliedRows,
      });
      this.rowHeights$.value = next;
      this.grid.onRowHeightsChanged();
    } else {
      traceTableLayout('flush-no-change', {
        groupId: this.groupId,
        options,
        rows: appliedRows,
      });
    }
  }

  private scheduleRowHeightFlush(
    options: { settleFrames?: number } = {}
  ): void {
    const settleFrames = options.settleFrames ?? 0;
    if (this.rowHeightFlushScheduled) {
      if (settleFrames <= 0) {
        traceTableLayout('flush-schedule-already-pending', {
          groupId: this.groupId,
          settleFrames,
          pendingSize: this.pendingRowHeights.size,
        });
        return;
      }
      if (this.rowHeightFlushTask != null) {
        cancelAnimationFrame(this.rowHeightFlushTask as number);
        clearTimeout(this.rowHeightFlushTask);
      }
      this.rowHeightFlushScheduled = false;
      this.rowHeightFlushTask = undefined;
    }
    this.rowHeightFlushScheduled = true;
    traceTableLayout('flush-schedule', {
      groupId: this.groupId,
      pending: Array.from(this.pendingRowHeights.entries()),
      scrolling: this.grid.container.isScrolling$.peek(),
      settleFrames,
    });
    if (this.grid.container.isScrolling$.peek()) {
      this.rowHeightFlushTask = setTimeout(() => this.flushRowHeights(), 120);
      return;
    }
    const schedule = (remainingFrames: number) => {
      this.rowHeightFlushTask = requestAnimationFrame(() => {
        if (remainingFrames > 0) {
          schedule(remainingFrames - 1);
          return;
        }
        this.flushRowHeights();
      });
    };
    schedule(settleFrames);
  }
}

export interface GridGroupData {
  id: string;
  rows: string[];
}

export interface GridVirtualScrollOptions<
  GroupData,
  RowData,
  CellData,
> extends VirtualScrollOptions {
  initGroupData: (group: GridGroup<GroupData, RowData, CellData>) => GroupData;
  initRowData: (row: GridRow<GroupData, RowData, CellData>) => RowData;
  initCellData: (cell: GridCell<GroupData, RowData, CellData>) => CellData;
  columns$: ReadonlySignal<
    {
      id: string;
      width: number;
    }[]
  >;
  fixedRowHeight$: ReadonlySignal<number | undefined>;
  estimatedRowHeight$: ReadonlySignal<number>;
  createGroup: {
    top: (
      group: GridGroup<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement;
    bottom: (
      group: GridGroup<GroupData, RowData, CellData>,
      wrapper: VirtualElementWrapper
    ) => HTMLElement;
  };
  createCell: (
    cell: GridCell<GroupData, RowData, CellData>,
    wrapper: VirtualElementWrapper
  ) => HTMLElement;
  groups$: ReadonlySignal<GridGroupData[]>;
  initialRowHeights?: ReadonlyMap<string, number>;
  onRowHeightsChange?: (rowHeights: ReadonlyMap<string, number>) => void;
}

export class GridVirtualScroll<
  GroupData,
  RowData,
  CellData,
> extends VirtualScroll {
  readonly cellsCache = new CacheManager<
    { groupId: string; columnId: string; rowId: string },
    GridCell<GroupData, RowData, CellData>
  >(cell => `${cell.groupId}-${cell.rowId}-${cell.columnId}`);
  readonly rowsCache = new CacheManager<
    { groupId: string; rowId: string },
    GridRow<GroupData, RowData, CellData>
  >(row => `${row.groupId}-${row.rowId}`);
  readonly groupsCache = new CacheManager<
    string,
    GridGroup<GroupData, RowData, CellData>
  >(groupId => groupId);

  readonly groups$: ReadonlySignal<GridGroup<GroupData, RowData, CellData>[]> =
    computed(() => {
      return this.groupIds$.value.map(groupId =>
        this.getOrCreateGroup(groupId)
      );
    });

  readonly groupIds$: ReadonlySignal<string[]> = computed(() => {
    return this.options.groups$.value.map(group => group.id);
  });

  constructor(
    readonly options: GridVirtualScrollOptions<GroupData, RowData, CellData>
  ) {
    super(options);
  }

  getOrCreateRow(
    group: GridGroup<GroupData, RowData, CellData>,
    rowId: string
  ): GridRow<GroupData, RowData, CellData> {
    return this.rowsCache.getOrCreate({ groupId: group.groupId, rowId }, () => {
      return new GridRow(group, rowId, this.options.initRowData);
    });
  }

  getGroup(groupId: string) {
    return this.getOrCreateGroup(groupId);
  }

  getRow(groupId: string, rowId: string) {
    const group = this.getOrCreateGroup(groupId);
    return this.getOrCreateRow(group, rowId);
  }

  getCell(groupId: string, rowId: string, columnId: string) {
    const row = this.getRow(groupId, rowId);
    return this.getOrCreateCell(row, columnId);
  }

  getOrCreateCell(
    row: GridRow<GroupData, RowData, CellData>,
    columnId: string
  ): GridCell<GroupData, RowData, CellData> {
    return this.cellsCache.getOrCreate(
      { groupId: row.group.groupId, rowId: row.rowId, columnId },
      () => {
        return new GridCell(
          row,
          columnId,
          this.options.createCell,
          this.options.initCellData
        );
      }
    );
  }

  getOrCreateGroup(groupId: string): GridGroup<GroupData, RowData, CellData> {
    return this.groupsCache.getOrCreate(groupId, () => {
      return new GridGroup(
        this,
        groupId,
        this.options.createGroup.top,
        this.options.createGroup.bottom,
        this.options.initGroupData
      );
    });
  }

  onRowHeightsChanged(): void {
    this.options.onRowHeightsChange?.(this.getRowHeightOverrides());
  }

  private getRowHeightOverrides(): ReadonlyMap<string, number> {
    const rowHeights = new Map<string, number>();
    for (const group of this.groups$.value) {
      for (const rowId of group.rowIds$.value) {
        const height = group.getRowHeight(rowId);
        if (height !== this.estimatedRowHeight$.value) {
          rowHeights.set(rowId, height);
        }
      }
    }
    return rowHeights;
  }

  private listenDataChange() {
    this.disposables.push(
      effect(() => {
        const activeColumnIds = new Set(
          this.columns$.value.map(column => column.id)
        );
        const activeGroupIds = new Set(
          this.options.groups$.value.map(group => group.id)
        );
        const activeRowIds = new Set<string>();

        for (const group of this.options.groups$.value) {
          for (const rowId of group.rows) {
            activeRowIds.add(
              this.rowsCache.keyToString({
                groupId: group.id,
                rowId,
              })
            );
          }
        }

        this.cellsCache.cleanupValues(cell => {
          const rowKey = this.rowsCache.keyToString({
            groupId: cell.row.group.groupId,
            rowId: cell.row.rowId,
          });
          return activeRowIds.has(rowKey) && activeColumnIds.has(cell.columnId);
        });
        this.rowsCache.cleanup(activeRowIds);
        this.groupsCache.cleanup(activeGroupIds);
      })
    );
  }

  private listenVisibleRows() {
    this.disposables.push(
      effect(() => {
        const viewport = this.container.viewport$.value;
        const activeColumnIds = new Set(
          this.columns$.value.map(column => column.id)
        );
        const activeRowKeys = new Set<string>();
        for (const group of this.groups$.value) {
          for (const rowId of group.getVisibleRowIds(viewport)) {
            activeRowKeys.add(
              this.rowsCache.keyToString({
                groupId: group.groupId,
                rowId,
              })
            );
            this.getOrCreateRow(group, rowId).cells$.value;
          }
        }
        this.cellsCache.cleanupValues(cell => {
          const rowKey = this.rowsCache.keyToString({
            groupId: cell.row.group.groupId,
            rowId: cell.row.rowId,
          });
          return (
            activeRowKeys.has(rowKey) && activeColumnIds.has(cell.columnId)
          );
        });
        this.rowsCache.cleanup(activeRowKeys);
      })
    );
  }

  lastGroupBottom$ = computed(() => {
    const lastGroup = this.groups$.value.findLast(
      group => group.bottom$.value != null
    );
    if (lastGroup == null) {
      return;
    }
    return lastGroup.bottom$.value;
  });

  override dispose() {
    super.dispose();
    this.cellsCache.clear();
    this.rowsCache.clear();
    this.groupsCache.clear();
  }

  get columns$() {
    return this.options.columns$;
  }

  get fixedRowHeight$() {
    return this.options.fixedRowHeight$;
  }

  get estimatedRowHeight$() {
    return this.options.estimatedRowHeight$;
  }

  columnPosition$ = computed(() => {
    const columns = this.options.columns$.value;
    const positions: { left: number; right: number; width: number }[] = [];
    let left = 0;
    for (const column of columns) {
      positions.push({
        left,
        right: left + column.width,
        width: column.width,
      });
      left += column.width ?? 0;
    }
    return positions;
  });

  totalWidth$ = computed(() => {
    const lastPosition =
      this.columnPosition$.value[this.columnPosition$.value.length - 1];
    if (lastPosition == null) {
      return 0;
    }
    return lastPosition.right;
  });

  get content() {
    return this.container.content;
  }

  override init() {
    super.init();
    this.container.init();
    this.listenSizeChange();
    this.listenDataChange();
    this.listenVisibleRows();
  }

  private listenSizeChange() {
    this.disposables.push(
      effect(() => {
        const width = this.totalWidth$.value ?? 0;
        const height = this.lastGroupBottom$.value ?? 0;
        this.container.updateContentSize(width, height);
      })
    );
  }
}

export interface VirtualScrollOptions {
  xScrollContainer?: HTMLElement;
  yScrollContainer?: HTMLElement;
}

export const getScrollContainer = (
  element: HTMLElement,
  direction: 'x' | 'y'
) => {
  let current: HTMLElement | null = element;
  while (current) {
    const overflow = current
      .computedStyleMap()
      .get(`overflow-${direction}`)
      ?.toString();
    if (overflow === 'auto' || overflow === 'scroll') {
      return current;
    }
    current = getComposedParentElement(current);
  }
  return;
};

function getComposedParentElement(element: HTMLElement): HTMLElement | null {
  if (element.parentElement) {
    return element.parentElement;
  }

  const root = element.getRootNode();
  if (
    typeof ShadowRoot !== 'undefined' &&
    root instanceof ShadowRoot &&
    root.host instanceof HTMLElement
  ) {
    return root.host;
  }

  return null;
}

export class VirtualScrollContainer {
  private readonly options: VirtualScrollOptions;
  private xScrollContainer?: HTMLElement;
  private readonly xScrollContainerWidth$ = signal(0);
  private yScrollContainer?: HTMLElement;
  private readonly yScrollContainerHeight$ = signal(0);
  readonly content: HTMLElement = document.createElement('div');
  readonly isScrolling$ = signal(false);
  readonly scrollTop$ = signal(0);
  readonly scrollLeft$ = signal(0);
  private readonly disposables: (() => void)[] = [];
  private scrollIdleTask: ReturnType<typeof setTimeout> | undefined;
  private readonly preloadSize = signal({
    left: 100,
    right: 100,
    top: 100,
    bottom: 100,
  });
  private readonly offsetTop$ = signal(0);
  private readonly offsetLeft$ = signal(0);
  private offsetLifecycleOwner: HTMLElement | undefined;
  readonly viewport$ = computed(() => {
    const preloadSize = this.preloadSize.value;
    const offsetTop = this.offsetTop$.value;
    const offsetLeft = this.offsetLeft$.value;
    const scrollTop = this.scrollTop$.value;
    const scrollLeft = this.scrollLeft$.value;
    const xScrollContainerWidth = this.xScrollContainerWidth$.value;
    const yScrollContainerHeight = this.yScrollContainerHeight$.value;
    const verticalPreload = getVirtualScrollVerticalPreloadSize(
      yScrollContainerHeight
    );
    const top = scrollTop - offsetTop - verticalPreload;
    const height = yScrollContainerHeight + verticalPreload * 2;
    const bottom = top + height;
    const left = scrollLeft - offsetLeft - preloadSize.left;
    const width = xScrollContainerWidth + preloadSize.left + preloadSize.right;
    const right = left + width;
    return {
      width,
      height,
      top,
      bottom,
      left,
      right,
    };
  });

  constructor(options: VirtualScrollOptions) {
    this.options = {
      ...options,
    };
  }

  init() {
    this.content.style.position = 'relative';
    this.content.style.overflow = 'hidden';
    this.xScrollContainer =
      this.options.xScrollContainer ??
      getScrollContainer(this.content, 'x') ??
      document.body;
    this.yScrollContainer =
      this.options.yScrollContainer ??
      getScrollContainer(this.content, 'y') ??
      document.body;
    this.listenScroll();
    this.listenResize();
    this.listenOffsetLifecycle();
    this.updateOffset();
  }

  private findOffsetLifecycleOwner(): HTMLElement | undefined {
    let current: HTMLElement | null = this.content;
    while (current) {
      if (
        current.classList.contains('block-active') ||
        current.classList.contains('block-idle')
      ) {
        return current;
      }
      current = getComposedParentElement(current);
    }
    return undefined;
  }

  private isOffsetMeasurementPaused(): boolean {
    return (
      !this.content.isConnected ||
      this.offsetLifecycleOwner?.classList.contains('block-idle') === true
    );
  }

  private listenOffsetLifecycle(): void {
    this.offsetLifecycleOwner = this.findOffsetLifecycleOwner();
    if (!this.offsetLifecycleOwner) return;

    const observer = new MutationObserver(() => {
      if (this.isOffsetMeasurementPaused()) {
        if (this.updateOffsetTask) {
          clearTimeout(this.updateOffsetTask);
          this.updateOffsetTask = undefined;
        }
        return;
      }
      this.updateOffset();
    });
    observer.observe(this.offsetLifecycleOwner, {
      attributeFilter: ['class'],
      attributes: true,
    });
    this.disposables.push(() => observer.disconnect());
  }

  private getOffset(
    container: HTMLElement,
    content: HTMLElement,
    direction: 'Top' | 'Left'
  ) {
    let current: HTMLElement | null = content;
    let offset = 0;
    while (current) {
      offset += current[`offset${direction}`];
      current =
        current.offsetParent instanceof HTMLElement
          ? current.offsetParent
          : null;
      if (current === container) {
        return offset;
      }
    }
    return;
  }
  private updateOffsetTask?: ReturnType<typeof setTimeout>;
  private updateOffset() {
    if (this.updateOffsetTask) {
      clearTimeout(this.updateOffsetTask);
      this.updateOffsetTask = undefined;
    }
    if (this.isOffsetMeasurementPaused()) {
      return;
    }
    if (this.yScrollContainer) {
      this.offsetTop$.value =
        this.getOffset(this.yScrollContainer, this.content, 'Top') ?? 0;
    }
    if (this.xScrollContainer) {
      this.offsetLeft$.value =
        this.getOffset(this.xScrollContainer, this.content, 'Left') ?? 0;
    }
    this.updateOffsetTask = setTimeout(() => {
      this.updateOffsetTask = undefined;
      this.updateOffset();
    }, 1000);
  }

  private listenScroll() {
    const markScrolling = () => {
      this.isScrolling$.value = true;
      if (this.scrollIdleTask) {
        clearTimeout(this.scrollIdleTask);
      }
      this.scrollIdleTask = setTimeout(() => {
        this.scrollIdleTask = undefined;
        this.isScrolling$.value = false;
      }, 120);
    };
    const handlerX = () => {
      markScrolling();
      this.scrollLeft$.value = this.xScrollContainer?.scrollLeft ?? 0;
    };
    const handlerY = () => {
      markScrolling();
      this.scrollTop$.value = this.yScrollContainer?.scrollTop ?? 0;
    };
    this.yScrollContainer?.addEventListener('scroll', handlerY);
    this.xScrollContainer?.addEventListener('scroll', handlerX);
    this.disposables.push(() => {
      this.yScrollContainer?.removeEventListener('scroll', handlerY);
      this.xScrollContainer?.removeEventListener('scroll', handlerX);
    });
  }

  private listenResize() {
    if (this.xScrollContainer) {
      const handlerX = () => {
        this.xScrollContainerWidth$.value =
          this.xScrollContainer?.clientWidth ?? 0;
      };
      const resizeObserver = new ResizeObserver(handlerX);
      resizeObserver.observe(this.xScrollContainer);
      this.disposables.push(() => {
        resizeObserver.disconnect();
      });
    }
    if (this.yScrollContainer) {
      const handlerY = () => {
        this.yScrollContainerHeight$.value =
          this.yScrollContainer?.clientHeight ?? 0;
      };
      const resizeObserver = new ResizeObserver(handlerY);
      resizeObserver.observe(this.yScrollContainer);
      this.disposables.push(() => {
        resizeObserver.disconnect();
      });
    }
  }
  readonly batchTaskManager = new BatchTaskManager([5, 50], 50);

  initElement(element: HTMLElement, isInit: ReadonlySignal<boolean>) {
    const initTask = this.batchTaskManager.newTask();
    const hideForMeasurement = () => {
      element.style.visibility = 'hidden';
      element.style.pointerEvents = 'none';
    };
    const showForPaint = () => {
      element.style.visibility = '';
      element.style.pointerEvents = '';
    };
    let desiredConnected = element.isConnected && isInit.peek();
    if (!isInit.peek()) {
      hideForMeasurement();
      initTask.updateTask(
        0,
        () => {
          if (element.isConnected || isInit.value) {
            return false;
          }
          hideForMeasurement();
          this.content.append(element);
          desiredConnected = false;
          return;
        },
        true
      );
    }
    const task = this.batchTaskManager.newTask();
    const hideTask = () => {
      if (!element.isConnected) {
        desiredConnected = false;
        return false;
      }
      element.remove();
      desiredConnected = false;
      return;
    };
    return {
      cancel: () => {
        initTask.cancel();
        task.cancel();
      },
      show: () => {
        if (!isInit.value) {
          desiredConnected = false;
          task.cancel();
          hideForMeasurement();
          if (!element.isConnected) {
            this.content.append(element);
          }
          return;
        }
        initTask.cancel();
        showForPaint();
        if (desiredConnected) {
          return;
        }
        desiredConnected = true;
        task.cancel();
        if (element.isConnected) {
          return;
        }
        this.content.append(element);
      },
      hide: () => {
        if (!desiredConnected && !element.isConnected) {
          return;
        }
        desiredConnected = false;
        if (!element.isConnected) {
          task.cancel();
          return;
        }
        task.updateTask(1, hideTask);
      },
    };
  }

  dispose() {
    if (this.scrollIdleTask) {
      clearTimeout(this.scrollIdleTask);
      this.scrollIdleTask = undefined;
    }
    if (this.updateOffsetTask) {
      clearTimeout(this.updateOffsetTask);
      this.updateOffsetTask = undefined;
    }
    this.batchTaskManager.clean();
    this.disposables.forEach(disposable => disposable());
  }

  public updateContentSize(width: number, height: number) {
    this.content.style.width = `${width}px`;
    this.content.style.height = `${height}px`;
  }

  public scrollToPosition(
    x: number,
    y: number,
    behavior: ScrollBehavior = 'auto'
  ) {
    this.xScrollContainer?.scrollTo({
      left: x,
      behavior,
    });
    this.yScrollContainer?.scrollTo({
      top: y,
      behavior,
    });
  }
}

export interface ListVirtualScrollOptions extends VirtualScrollOptions {
  itemCount: number;
  itemHeight: number | ((index: number) => number);
}

export class ListVirtualScroll extends VirtualScroll {
  protected itemCount: number;
  protected itemHeight: number | ((index: number) => number);

  constructor(options: ListVirtualScrollOptions) {
    super(options);
    this.itemCount = options.itemCount;
    this.itemHeight = options.itemHeight;
    this.updateTotalSize();
  }

  private updateTotalSize() {}
}
